import React, { useCallback, useEffect, useRef, useState } from "react";
import Headers from "../components/Headers";
import Footer from "../components/Footer";
import { Link, useNavigate, useParams } from "react-router-dom";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { useDispatch, useSelector } from "react-redux";
import Ratings from "../components/Ratings";
import Reviews from "../components/Reviews";
import ProductPageSkeleton from "../components/skeletons/ProductPageSkeleton";
import ProductCardSkeleton from "../components/skeletons/ProductCardSkeleton";
import ProductShareButton from "../components/products/ProductShareButton";
import ProductImage from "../components/ProductImage";
import { get_product } from "../store/reducers/homeReducer";
import {
  add_to_card,
  add_to_wishlist,
  messageClear,
} from "../store/reducers/cardReducer";
import toast from "react-hot-toast";
import { fetchShippingFee } from "../utils/shippingFee";
import api from "../api/api";

const makeVariantKey = (attributes = []) =>
  [...attributes]
    .map((item) => ({
      name: String(item.name || "").trim(),
      value: String(item.value || "").trim(),
    }))
    .filter((item) => item.name && item.value)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((item) => `${item.name}:${item.value}`)
    .join("|");

const Details = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const dispatch = useDispatch();

  const { product, relatedProducts, totalReview, productLoading } = useSelector(
    (state) => state.home,
  );
  const { userInfo } = useSelector((state) => state.auth);
  const { errorMessage, successMessage, cartUpdating } = useSelector(
    (state) => state.card,
  );

  const [activeTab, setActiveTab] = useState("description");
  const [quantity, setQuantity] = useState(1);
  const [imageIndex, setImageIndex] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [gallerySwiper, setGallerySwiper] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [pincode, setPincode] = useState("");
  const [pincodeResult, setPincodeResult] = useState(null);
  const [pincodeLoading, setPincodeLoading] = useState(false);
  const thumbnailRefs = useRef([]);

  const images = product.images || [];
  const galleryImages = images.length ? images : [null];
  const discount = Number(product.discount) || 0;
  const productVariations = product.variations || [];
  const selectedVariantKey = makeVariantKey(
    Object.entries(selectedAttributes).map(([name, value]) => ({
      name,
      value,
    })),
  );
  const selectedVariant = (product.variantCombinations || []).find(
    (item) => item.isActive !== false && item.variantKey === selectedVariantKey,
  );
  const effectiveStock = selectedVariant?.stock ?? product.stock;
  const effectiveBasePrice = selectedVariant?.price || product.price || 0;
  const finalPrice =
    discount > 0
      ? effectiveBasePrice - Math.floor((effectiveBasePrice * discount) / 100)
      : effectiveBasePrice;
  const shareImage = galleryImages[imageIndex] || galleryImages[0] || "";

  const metaRows = [
    { label: "Brand", value: product.brand || "MyHaat" },
    { label: "Category", value: product.category || "-" },
    { label: "Seller", value: product.shopName || "MyHaat Store" },
    { label: "Fabric", value: product.brand || "Premium Fabric" },
  ];

  const specificationRows = [
    { label: "Brand", value: product.brand || "MyHaat" },
    { label: "Category", value: product.category || "-" },
    { label: "Discount", value: discount ? `${discount}%` : "0%" },
    { label: "Rating", value: `${Number(product.rating || 0).toFixed(1)} / 5` },
    { label: "Stock", value: product.stock || 0 },
  ];

  useEffect(() => {
    dispatch(get_product(slug));
  }, [dispatch, slug]);

  useEffect(() => {
    setQuantity(1);
    setImageIndex(0);
    setPreviewOpen(false);
    setLoadedImages({});
    setSelectedAttributes({});
    setPincode("");
    setPincodeResult(null);
  }, [product._id]);

  useEffect(() => {
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
  }, [dispatch, errorMessage, successMessage]);

  const syncImage = useCallback(
    (nextIndex) => {
      if (!images.length) return;
      const normalizedIndex = (nextIndex + images.length) % images.length;
      setImageIndex(normalizedIndex);
      if (gallerySwiper) {
        gallerySwiper.slideTo(normalizedIndex);
      }
    },
    [gallerySwiper, images.length],
  );

  const openImagePreview = (index) => {
    if (!images.length) return;
    syncImage(index);
    setPreviewOpen(true);
  };

  useEffect(() => {
    const activeThumb = thumbnailRefs.current[imageIndex];
    if (activeThumb) {
      activeThumb.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [imageIndex]);

  useEffect(() => {
    if (!previewOpen) return undefined;

    const originalOverflow = document.body.style.overflow;
    const handlePreviewKeyDown = (event) => {
      if (event.key === "Escape") {
        setPreviewOpen(false);
      }

      if (event.key === "ArrowLeft") {
        syncImage(imageIndex - 1);
      }

      if (event.key === "ArrowRight") {
        syncImage(imageIndex + 1);
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handlePreviewKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handlePreviewKeyDown);
    };
  }, [imageIndex, previewOpen, syncImage]);

  const handleImageLoad = (imageKey) => {
    setLoadedImages((prev) => ({
      ...prev,
      [imageKey]: true,
    }));
  };

  const inc = () => {
    if (quantity >= effectiveStock) {
      toast.error("Out of stock");
      return;
    }
    setQuantity((prev) => prev + 1);
  };

  const dec = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  const getSelectedVariationForSubmit = () => {
    if (!productVariations.length) return null;

    const missing = productVariations.find(
      (variation) =>
        variation.required !== false && !selectedAttributes[variation.name],
    );

    if (missing) {
      toast.error(`Please select ${missing.label || missing.name}`);
      return false;
    }

    if (!selectedVariant) {
      toast.error("Please select an available variation combination");
      return false;
    }

    return {
      variantKey: selectedVariant.variantKey,
      sku: selectedVariant.sku || "",
      attributes: selectedVariant.attributes || [],
    };
  };

  const addCard = (productId = product._id, selectedQuantity = quantity) => {
    if (!userInfo) {
      navigate("/login");
      return;
    }

    const selectedVariation =
      productId === product._id ? getSelectedVariationForSubmit() : null;
    if (selectedVariation === false) return;

    dispatch(
      add_to_card({
        userId: userInfo.id,
        quantity: selectedQuantity,
        productId,
        selectedVariation,
      }),
    );
  };

  const addWishlist = (selectedProduct = product) => {
    if (!userInfo) {
      navigate("/login");
      return;
    }

    dispatch(
      add_to_wishlist({
        userId: userInfo.id,
        productId: selectedProduct._id,
        name: selectedProduct.name,
        price: selectedProduct.price,
        image: selectedProduct.images?.[0] || "",
        discount: selectedProduct.discount,
        rating: selectedProduct.rating,
        slug: selectedProduct.slug,
      }),
    );
  };

  const buy = async () => {
    if (!userInfo) {
      navigate("/login");
      return;
    }

    const computedPrice =
      discount > 0
        ? effectiveBasePrice -
          Math.floor((effectiveBasePrice * product.discount) / 100)
        : effectiveBasePrice;

    const shippingFee = await fetchShippingFee(1);
    const selectedVariation = getSelectedVariationForSubmit();
    if (selectedVariation === false) return;

    navigate("/shipping", {
      state: {
        products: [
          {
            sellerId: product.sellerId,
            shopName: product.shopName,
            price: computedPrice * quantity,
            products: [
              {
                quantity,
                selectedVariation,
                productInfo: {
                  ...product,
                  price: effectiveBasePrice,
                  selectedVariation,
                },
              },
            ],
          },
        ],
        price: computedPrice * quantity,
        shipping_fee: shippingFee,
        items: 1,
      },
    });
  };

  const selectVariationOption = (variation, option) => {
    setSelectedAttributes((current) => ({
      ...current,
      [variation.name]: option.value,
    }));
  };

  const checkPincode = async () => {
    if (!/^\d{6}$/.test(pincode.trim())) {
      setPincodeResult({
        available: false,
        message: "Please enter a valid 6-digit pincode.",
      });
      return;
    }

    setPincodeLoading(true);
    try {
      const { data } = await api.get(
        `/home/product/${product._id}/check-pincode/${pincode.trim()}`,
      );
      setPincodeResult(data);
    } catch (error) {
      setPincodeResult(
        error.response?.data || {
          available: false,
          message: "Unable to check pincode.",
        },
      );
    } finally {
      setPincodeLoading(false);
    }
  };

  if (productLoading && !product?._id) {
    return (
      <div className="min-h-screen bg-[var(--mh-bg)]">
        <Headers />
        <ProductPageSkeleton />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--mh-bg)]">
      <Headers />

      <div className="mx-auto max-w-[1200px] px-5 pt-5 sm:px-4">
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
          <Link className="hover:text-[var(--mh-primary)]" to="/">
            Home
          </Link>
          <MdOutlineKeyboardArrowRight />
          <Link className="hover:text-[var(--mh-primary)]" to="/">
            {product.category || "Products"}
          </Link>
          <MdOutlineKeyboardArrowRight />
          <span className="text-slate-700">{product.name}</span>
        </div>
      </div>

      <section className="mx-auto max-w-[1200px] px-5 pb-10 pt-5 sm:px-4">
        <div className="grid grid-cols-[45%_55%] gap-8 lg:gap-6 md-lg:grid-cols-1 md-lg:gap-5">
          <div className="min-w-0">
            <div className="grid grid-cols-[80px_minmax(0,1fr)] gap-4 md:grid-cols-1">
              {images.length >= 1 && (
                <div className="max-h-[420px] overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden md:hidden">
                  <div className="flex flex-col gap-2">
                    {images.map((image, index) => (
                      <button
                        key={`${image}-${index}-desktop-thumb`}
                        ref={(element) => {
                          thumbnailRefs.current[index] = element;
                        }}
                        aria-label={`View image ${index + 1}`}
                        className={`overflow-hidden rounded-[6px] bg-white ${
                          imageIndex === index
                            ? "border-2 border-[var(--mh-primary)]"
                            : "border border-[#eee] hover:border-[#ffd1b0]"
                        }`}
                        onClick={() => syncImage(index)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            syncImage(index);
                          }
                        }}
                        type="button"
                      >
                        <ProductImage
                          alt={`${product.name || "Product"} ${index + 1}`}
                          className="w-[80px]"
                          imgClassName="p-1"
                          loading="lazy"
                          src={image}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="min-w-0">
                <div className="relative min-w-0 overflow-hidden rounded-[8px] bg-white">
                  <Swiper
                    allowTouchMove={true}
                    className="w-full"
                    onSlideChange={(swiper) =>
                      setImageIndex(swiper.activeIndex)
                    }
                    onSwiper={setGallerySwiper}
                    slidesPerView={1}
                  >
                    {galleryImages.map((image, index) => {
                      const imageKey = image || "fallback-image";

                      return (
                        <SwiperSlide key={`${imageKey}-slide-${index}`}>
                          <div className="relative overflow-hidden rounded-[8px] border border-[var(--mh-border)] bg-white sm:h-auto">
                            {!loadedImages[imageKey] && (
                              <div className="absolute inset-0 skeleton rounded-[8px]" />
                            )}
                            <button
                              aria-label={`Open image ${index + 1} preview`}
                              className="block w-full cursor-zoom-in text-left disabled:cursor-default"
                              disabled={!image}
                              onClick={() => openImagePreview(index)}
                              type="button"
                            >
                              <ProductImage
                                alt={product.name || "Product"}
                                className="w-full rounded-[8px]"
                                imgClassName={`p-5 transition-opacity duration-200 sm:p-4 ${
                                  loadedImages[imageKey]
                                    ? "opacity-100"
                                    : "opacity-0"
                                }`}
                                loading={index === 0 ? "eager" : "lazy"}
                                onLoad={() => handleImageLoad(imageKey)}
                                src={image}
                              />
                            </button>
                          </div>
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>

                  {images.length > 1 && (
                    <>
                      <button
                        aria-label="Previous image"
                        className="absolute  sm:hidden left-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm hover:text-[var(--mh-primary)]"
                        onClick={() => syncImage(imageIndex - 1)}
                        type="button"
                      >
                        <FiChevronLeft size={18} />
                      </button>
                      <button
                        aria-label="Next image"
                        className="absolute sm:hidden right-3 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm hover:text-[var(--mh-primary)]"
                        onClick={() => syncImage(imageIndex + 1)}
                        type="button"
                      >
                        <FiChevronRight size={18} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {images.length > 1 && (
              <div className="mt-3 hidden gap-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden md:flex">
                {images.map((image, index) => (
                  <button
                    key={`${image}-${index}-mobile-thumb`}
                    ref={(element) => {
                      thumbnailRefs.current[index] = element;
                    }}
                    aria-label={`View image ${index + 1}`}
                    className={`flex-shrink-0 overflow-hidden rounded-[6px] bg-white ${
                      imageIndex === index
                        ? "border-2 border-[var(--mh-primary)]"
                        : "border border-[#eee] hover:border-[#ffd1b0]"
                    }`}
                    onClick={() => syncImage(index)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        syncImage(index);
                      }
                    }}
                    type="button"
                  >
                    <ProductImage
                      alt={`${product.name || "Product"} ${index + 1}`}
                      className="w-[70px]"
                      imgClassName="p-1"
                      loading="lazy"
                      src={image}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <h1 className="text-[22px] font-semibold leading-[1.4] text-[var(--mh-ink)]">
                {product.name}
              </h1>

              <ProductShareButton
                className="shrink-0"
                productImage={shareImage}
                productName={product.name}
                productPrice={finalPrice}
              />
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3">
              <div className="flex text-base text-[#ffb347]">
                <Ratings ratings={product.rating} />
              </div>
              <span className="text-sm text-slate-500">
                {totalReview} reviews
              </span>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="text-[28px] font-bold leading-none text-[var(--mh-ink)]">
                ₹{finalPrice}
              </span>
              {discount > 0 && (
                <>
                  <span className="text-sm text-slate-400 line-through">
                    ₹{product.price}
                  </span>
                  <span className="rounded-full bg-[#fff1e8] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--mh-primary)]">
                    {discount}% OFF
                  </span>
                </>
              )}
            </div>

            <div
              className={`mt-3 text-sm font-medium ${
                effectiveStock ? "text-green-600" : "text-red-600"
              }`}
            >
              {effectiveStock ? `In Stock (${effectiveStock})` : "Out of Stock"}
            </div>

            {productVariations.length > 0 && (
              <div className="mt-5 space-y-4">
                {productVariations.map((variation) => (
                  <div key={variation.name}>
                    <p className="mb-2 text-sm font-medium text-[var(--mh-ink)]">
                      {variation.label || variation.name}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(variation.selectedOptions || []).map((option) => {
                        const active =
                          selectedAttributes[variation.name] === option.value;
                        return (
                          <button
                            key={`${variation.name}-${option.value}`}
                            type="button"
                            onClick={() =>
                              selectVariationOption(variation, option)
                            }
                            className={`rounded-md border px-3 py-2 text-sm ${
                              active
                                ? "border-[var(--mh-primary)] bg-[#fff1e8] text-[var(--mh-primary)]"
                                : "border-[var(--mh-border)] bg-white text-slate-700 hover:border-[#ffd1b0]"
                            }`}
                          >
                            {option.group ? `${option.group} - ` : ""}
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-5 rounded-[8px] border border-[var(--mh-border)] bg-white p-4">
              <label
                className="mb-2 block text-sm font-medium text-[var(--mh-ink)]"
                htmlFor="delivery-pincode"
              >
                Deliver to
              </label>
              <div className="flex gap-2 sm:flex-col">
                <input
                  id="delivery-pincode"
                  className="h-[42px] min-w-0 flex-1 rounded-md border border-[var(--mh-border)] px-3 text-sm outline-none focus:border-[var(--mh-primary)]"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                  placeholder="Enter pincode"
                  maxLength={6}
                />
                <button
                  className="h-[42px] rounded-md bg-[var(--mh-ink)] px-4 text-sm font-semibold text-white disabled:opacity-60"
                  type="button"
                  disabled={pincodeLoading}
                  onClick={checkPincode}
                >
                  {pincodeLoading ? "Checking..." : "Check"}
                </button>
              </div>
              {pincodeResult && (
                <p
                  className={`mt-2 text-sm ${pincodeResult.available ? "text-green-600" : "text-red-600"}`}
                >
                  {pincodeResult.available
                    ? "Delivery available"
                    : pincodeResult.message ||
                      "Delivery not available to this pincode."}
                </p>
              )}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3 sm:flex-col sm:items-stretch">
              <div className="flex h-[42px] items-center justify-between rounded-md border border-[var(--mh-border)] bg-white px-3 sm:w-fit">
                <button
                  aria-label="Decrease quantity"
                  className="px-2 text-lg font-semibold text-slate-700 hover:text-[var(--mh-primary)]"
                  onClick={dec}
                  type="button"
                >
                  -
                </button>
                <span className="min-w-[28px] text-center text-sm font-semibold text-[var(--mh-ink)]">
                  {quantity}
                </span>
                <button
                  aria-label="Increase quantity"
                  className="px-2 text-lg font-semibold text-slate-700 hover:text-[var(--mh-primary)]"
                  onClick={inc}
                  type="button"
                >
                  +
                </button>
              </div>

              <button
                className="flex h-[42px] items-center justify-center rounded-md bg-[var(--mh-primary)] px-5 text-sm font-semibold text-white hover:bg-[var(--mh-primary-dark)] disabled:opacity-70 sm:w-full"
                disabled={!effectiveStock || cartUpdating}
                onClick={() => addCard()}
                type="button"
              >
                {cartUpdating ? "Updating..." : "Add to Cart"}
              </button>

              <button
                className="flex h-[42px] items-center justify-center rounded-md border border-[var(--mh-ink)] px-5 text-sm font-semibold text-[var(--mh-ink)] hover:bg-[var(--mh-ink)] hover:text-white disabled:opacity-60 sm:w-full"
                disabled={!effectiveStock}
                onClick={buy}
                type="button"
              >
                Buy Now
              </button>
            </div>

            <button
              aria-label="Add to wishlist"
              className="mt-3 flex h-[42px] items-center gap-2 rounded-md text-sm font-medium text-slate-500 hover:text-[var(--mh-primary)]"
              onClick={() => addWishlist()}
              type="button"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-sm">
                <span className="text-[var(--mh-primary)]">&#10084;</span>
              </span>
              Add to wishlist
            </button>

            <div className="mt-6 grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-1 sm:gap-y-2">
              {metaRows.map((item) => (
                <div
                  key={item.label}
                  className="grid grid-cols-[86px_minmax(0,1fr)] gap-3"
                >
                  <span className="text-sm text-slate-400">{item.label}</span>
                  <span className="text-sm text-slate-700">{item.value}</span>
                </div>
              ))}
            </div>

            <div className="mt-4 text-sm text-slate-500">
              Sold by{" "}
              <Link
                className="font-medium text-[var(--mh-primary)] hover:text-[var(--mh-primary-dark)]"
                to={`/dashboard/chat/${product.sellerId}`}
              >
                {product.shopName || "MyHaat Store"}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-5 pb-10 sm:px-4">
        <div className="border-b border-[var(--mh-border)]">
          <div className="flex gap-6 overflow-x-auto">
            {[
              { key: "description", label: "Description" },
              { key: "specifications", label: "Specifications" },
              { key: "reviews", label: "Reviews" },
            ].map((tab) => (
              <button
                key={tab.key}
                className={`relative whitespace-nowrap pb-3 text-sm font-semibold ${
                  activeTab === tab.key
                    ? "text-[var(--mh-primary)]"
                    : "text-slate-500 hover:text-[var(--mh-primary)]"
                }`}
                onClick={() => setActiveTab(tab.key)}
                type="button"
              >
                {tab.label}
                {activeTab === tab.key && (
                  <span className="absolute bottom-0 left-0 h-[2px] w-full rounded-full bg-[var(--mh-primary)]" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="pt-5">
          {activeTab === "description" && (
            <div
              className="text-[14px] leading-[1.6] text-slate-600 [&_h1]:mb-3 [&_h1]:text-lg [&_h1]:font-semibold [&_h2]:mb-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold [&_li]:ml-5 [&_li]:list-disc [&_ol]:ml-5 [&_ol]:list-decimal [&_p]:mb-3 [&_ul]:mb-3"
              dangerouslySetInnerHTML={{
                __html:
                  product.description ||
                  "No description available for this product.",
              }}
            />
          )}

          {activeTab === "specifications" && (
            <div className="grid grid-cols-2 gap-x-10 gap-y-3 md:grid-cols-1">
              {specificationRows.map((item) => (
                <div
                  key={item.label}
                  className="grid grid-cols-[90px_minmax(0,1fr)] gap-3"
                >
                  <span className="text-sm text-slate-400">{item.label}</span>
                  <span className="text-sm text-slate-700">{item.value}</span>
                </div>
              ))}
            </div>
          )}

          {activeTab === "reviews" && <Reviews product={product} />}
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-5 pb-14 sm:px-4">
        <h2 className="mb-5 text-xl font-semibold text-[var(--mh-ink)]">
          Related Products
        </h2>

        {productLoading ? (
          <div className="grid grid-cols-4 gap-4 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1">
            {Array.from({ length: 4 }).map((_, index) => (
              <ProductCardSkeleton key={index} compact />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-4 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1">
            {relatedProducts.map((item) => {
              const itemDiscount = Number(item.discount) || 0;
              const itemPrice =
                itemDiscount > 0
                  ? item.price - Math.floor((item.price * itemDiscount) / 100)
                  : item.price;

              return (
                <article
                  key={item._id}
                  className="overflow-hidden rounded-xl bg-white shadow-[0_8px_22px_rgba(15,23,42,0.06)] transition-transform duration-200 hover:-translate-y-1"
                >
                  <Link to={`/product/details/${item.slug}`}>
                    <ProductImage
                      alt={item.name}
                      className="w-full"
                      imgClassName="p-4"
                      loading="lazy"
                      src={item.images?.[0]}
                    />
                  </Link>

                  <div className="p-3">
                    <Link
                      className="line-clamp-2 text-sm font-medium leading-6 min-h-[calc(1.5rem*2)] text-[var(--mh-ink)] hover:text-[var(--mh-primary)]"
                      to={`/product/details/${item.slug}`}
                    >
                      {item.name}
                    </Link>
                    <div className="mt-2 flex items-end gap-2">
                      <span className="text-lg font-bold text-[var(--mh-ink)]">
                        ₹{itemPrice}
                      </span>
                      {itemDiscount > 0 && (
                        <span className="text-xs text-slate-400 line-through">
                          ₹{item.price}
                        </span>
                      )}
                    </div>
                    <button
                      className="mt-3 flex h-[42px] w-full items-center justify-center rounded-md bg-[var(--mh-primary)] px-4 text-sm font-semibold text-white hover:bg-[var(--mh-primary-dark)]"
                      onClick={() => addCard(item._id, 1)}
                      type="button"
                    >
                      Add to Cart
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {previewOpen && images.length > 0 && (
        <div
          aria-label="Product image preview"
          aria-modal="true"
          className="fixed inset-0 z-[100000] bg-black/80 px-5 py-5 sm:px-3 sm:py-3"
          onClick={() => setPreviewOpen(false)}
          role="dialog"
        >
          <div
            className="relative mx-auto flex h-full max-w-[1100px] flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex justify-end pb-3">
              <button
                aria-label="Close image preview"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-700 shadow-lg hover:text-[var(--mh-primary)]"
                onClick={() => setPreviewOpen(false)}
                type="button"
              >
                <FiX size={22} />
              </button>
            </div>

            <div className="relative min-h-0 flex-1">
              <div className="flex h-full items-center justify-center rounded-[8px] bg-white p-3 shadow-2xl sm:p-2">
                <ProductImage
                  alt={`${product.name || "Product"} preview`}
                  aspectRatio={null}
                  backgroundClassName="bg-white"
                  className="h-[72vh] max-h-[720px] w-full rounded-[8px] md-lg:h-[62vh] sm:h-[55vh]"
                  imgClassName="p-2 sm:p-1"
                  loading="eager"
                  src={images[imageIndex] || images[0]}
                />
              </div>

              {images.length > 1 && (
                <>
                  <button
                    aria-label="Previous preview image"
                    className="absolute left-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-slate-700 shadow-md hover:text-[var(--mh-primary)] sm:left-2 sm:h-9 sm:w-9"
                    onClick={() => syncImage(imageIndex - 1)}
                    type="button"
                  >
                    <FiChevronLeft size={20} />
                  </button>
                  <button
                    aria-label="Next preview image"
                    className="absolute right-4 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-slate-700 shadow-md hover:text-[var(--mh-primary)] sm:right-2 sm:h-9 sm:w-9"
                    onClick={() => syncImage(imageIndex + 1)}
                    type="button"
                  >
                    <FiChevronRight size={20} />
                  </button>
                </>
              )}
            </div>

            {images.length > 1 && (
              <div className="mt-4 flex justify-center gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                {images.map((image, index) => (
                  <button
                    key={`${image}-${index}-preview-thumb`}
                    aria-label={`Preview image ${index + 1}`}
                    className={`h-[70px] w-[70px] flex-shrink-0 overflow-hidden rounded-[6px] bg-white ${
                      imageIndex === index
                        ? "border-2 border-[var(--mh-primary)]"
                        : "border border-white/60 hover:border-[#ffd1b0]"
                    } sm:h-[58px] sm:w-[58px]`}
                    onClick={() => syncImage(index)}
                    type="button"
                  >
                    <ProductImage
                      alt={`${product.name || "Product"} ${index + 1}`}
                      backgroundClassName="bg-white"
                      className="h-full w-full"
                      imgClassName="p-1"
                      loading="lazy"
                      src={image}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Details;
